import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

interface DisableRetryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading: boolean;
}

/**
 * Confirmation for turning OFF message retries. With retries off, a message
 * that fails to reach the customer's app the first time is never tried again
 * and is lost. Destructive enough to warrant an explicit approval step
 * (mirrors DisableForwardingDialog, minus the type-to-confirm gate since this
 * is reversible at any time). Copy is plain and user-facing — no internal
 * "forwarder returns 200 to Meta" wording.
 */
export function DisableRetryDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: DisableRetryDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!loading) onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Stop retrying deliveries for this channel?</AlertDialogTitle>
          <AlertDialogDescription>
            Right now, when an event can&rsquo;t reach your app we keep trying
            to deliver it so nothing is lost. If you turn this off, an event
            that fails to arrive the first time is gone for good. Only turn it
            off if you&rsquo;re sure you don&rsquo;t need failed deliveries tried
            again.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep Retrying</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading && <Loader2 className="size-3.5 animate-spin" />}
            {loading ? 'Turning Off...' : 'Stop Retrying'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
